import { useState } from 'react';
import { useCreateCertificate } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/Dialog';
import { CID10Autocomplete } from '@/components/medical-records/CID10Autocomplete';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import type { CertificateType } from '@/api';

interface CertificateEditorProps {
  patientId: string;
  appointmentId?: string;
  medicalRecordId: string;
  doctorId: string;
  open: boolean;
  onSuccess: () => void;
  onClose: () => void;
}

const certificateTypeOptions: { value: CertificateType; label: string }[] = [
  { value: 'SICK_NOTE', label: 'Atestado de Doença' },
  { value: 'HEALTH_CERTIFICATE', label: 'Atestado de Saúde' },
  { value: 'TRAVEL_CERTIFICATE', label: 'Atestado de Viagem' },
  { value: 'OTHER', label: 'Outro' },
];

const restrictionTypeOptions = [
  { value: 'REST', label: 'Repouso completo' },
  { value: 'PARTIAL', label: 'Atividades leves' },
  { value: 'MEDICAL_CARE', label: 'Cuidados médicos' },
  { value: 'MEDICATION', label: 'Medicação' },
];

export function CertificateEditor({
  patientId,
  appointmentId,
  medicalRecordId,
  doctorId,
  open,
  onSuccess,
  onClose,
}: CertificateEditorProps) {
  const [type, setType] = useState<CertificateType>('SICK_NOTE');
  const [cidCode, setCidCode] = useState('');
  const [, setCidDescription] = useState('');
  const [daysOff, setDaysOff] = useState('');
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0] ?? '');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [observations, setObservations] = useState('');
  const [restrictionType, setRestrictionType] = useState('');
  const [sendToMemed, setSendToMemed] = useState(false);

  const createCertificate = useCreateCertificate();

  const handleSubmit = async (e: React.FormEvent, sendToMemedFlag: boolean) => {
    e.preventDefault();

    if (!reason.trim()) return;
    if (!daysOff || parseInt(daysOff) <= 0) return;

    try {
      const certificate = await createCertificate.mutateAsync({
        medicalRecordId,
        patientId,
        doctorId,
        appointmentId: appointmentId ?? '',
        type,
        startDate,
        endDate: endDate || undefined,
        daysOff: parseInt(daysOff),
        reason: reason.trim(),
        observations: observations.trim() || undefined,
      });

      if (sendToMemedFlag && certificate.id) {
        const { memedApi } = await import('@/api/memed');
        await memedApi.sendCertificateToMemed(certificate.id);
      }

      handleClose();
      onSuccess();
    } catch (error) {
      console.error('Failed to create certificate:', error);
    }
  };

  const handleClose = () => {
    setType('SICK_NOTE');
    setCidCode('');
    setCidDescription('');
    setDaysOff('');
    setStartDate(new Date().toISOString().split('T')[0] ?? '');
    setEndDate('');
    setReason('');
    setObservations('');
    setRestrictionType('');
    setSendToMemed(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Emitir Atestado</DialogTitle>
        </DialogHeader>

        {createCertificate.isPending && (
          <LoadingOverlay message="Emitindo atestado..." />
        )}

        <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Atestado *
            </label>
            <Select value={type} onValueChange={(v) => setType(v as CertificateType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {certificateTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Início *
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Término
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dias de Afastamento *
            </label>
            <Input
              type="number"
              value={daysOff}
              onChange={(e) => setDaysOff(e.target.value)}
              placeholder="Número de dias"
              min={1}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CID-10 (opcional)
            </label>
            <CID10Autocomplete
              value={cidCode}
              onChange={(code, desc) => {
                setCidCode(code);
                setCidDescription(desc);
              }}
              placeholder="Buscar código CID-10..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Restrição (opcional)
            </label>
            <Select value={restrictionType} onValueChange={setRestrictionType}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo de restrição" />
              </SelectTrigger>
              <SelectContent>
                {restrictionTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Justificativa Médica *
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Descreva o motivo do atestado..."
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações (opcional)
            </label>
            <Textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Observações adicionais..."
              rows={2}
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="sendToMemed"
              checked={sendToMemed}
              onChange={(e) => setSendToMemed(e.target.checked)}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label htmlFor="sendToMemed" className="text-sm text-gray-700">
              Enviar para MeMed após emitir
            </label>
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!reason.trim() || !daysOff || createCertificate.isPending}
            >
              {createCertificate.isPending ? 'Emitindo...' : 'Emitir Atestado'}
            </Button>
            {sendToMemed && (
              <Button
                type="button"
                disabled={!reason.trim() || !daysOff || createCertificate.isPending}
                onClick={(e) => handleSubmit(e as any, true)}
              >
                Emitir e Enviar para MeMed
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
